import React, { useState, useEffect, useRef } from 'react';
import { User, X, Clock, Loader2, ArrowLeft, Download, MapPin, Save, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../utils/api';
import { LoadingSpinner } from '../common/Badges';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

const LiveMapView = ({ techs, fmtTime, fmtDuration }) => {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const trailsRef = useRef([]);

  useEffect(() => {
    if (!mapContainerRef.current) return;
    // Init map once
    if (!mapInstanceRef.current) {
      const firstLoc = techs.find(t => t.last_location)?.last_location;
      const center = firstLoc ? [firstLoc.lat, firstLoc.lng] : [40.7128, -74.006];
      mapInstanceRef.current = L.map(mapContainerRef.current).setView(center, 14);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: 'BELLAJA Electric' }).addTo(mapInstanceRef.current);
    }
    return () => {
      if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update markers and trails whenever techs change
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // Clear old markers and trails
    markersRef.current.forEach(m => map.removeLayer(m));
    markersRef.current = [];
    trailsRef.current.forEach(t => map.removeLayer(t));
    trailsRef.current = [];

    const positions = [];

    techs.forEach(tech => {
      // Draw trail polyline
      if (tech.location_trail?.length > 1) {
        const trailCoords = tech.location_trail.map(p => [p.lat, p.lng]);
        const polyline = L.polyline(trailCoords, {
          color: tech.status === 'working' ? '#dc2626' : '#eab308',
          weight: 3,
          opacity: 0.7,
          dashArray: tech.status === 'lunch' ? '8, 8' : null
        }).addTo(map);
        trailsRef.current.push(polyline);

        // Add small dot markers along the trail
        tech.location_trail.slice(0, -1).forEach((point, idx) => {
          const opacity = 0.3 + (idx / tech.location_trail.length) * 0.5;
          const dot = L.circleMarker([point.lat, point.lng], {
            radius: 4,
            fillColor: tech.status === 'working' ? '#dc2626' : '#eab308',
            fillOpacity: opacity,
            color: '#fff',
            weight: 1,
            opacity: 0.4
          }).addTo(map);
          dot.bindPopup(`<b>${tech.technician_name}</b><br/>Pika GPS #${idx + 1}<br/>${fmtTime(point.time)}`);
          markersRef.current.push(dot);
        });
      }

      // Add main marker for current position
      if (tech.last_location) {
        positions.push([tech.last_location.lat, tech.last_location.lng]);
        const icon = L.divIcon({
          html: `<div style="background:#dc2626;width:36px;height:36px;border-radius:50%;border:3px solid #fff;box-shadow:0 3px 12px rgba(220,38,38,0.5);display:flex;align-items:center;justify-content:center">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          </div>`,
          className: '',
          iconSize: [36, 36],
          iconAnchor: [18, 36],
          popupAnchor: [0, -38]
        });
        const marker = L.marker([tech.last_location.lat, tech.last_location.lng], { icon }).addTo(map);
        const statusText = tech.status === 'working' ? 'Duke Punuar' : 'Pushim Dreke';
        const statusColor = tech.status === 'working' ? '#16a34a' : '#ca8a04';
        marker.bindPopup(`
          <div style="min-width:200px;font-family:system-ui">
            <p style="font-weight:bold;font-size:14px;margin:0 0 6px">${tech.technician_name}</p>
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px">
              <span style="width:8px;height:8px;border-radius:50%;background:${statusColor};display:inline-block"></span>
              <span style="font-size:12px;font-weight:600;color:${statusColor}">${statusText}</span>
            </div>
            <div style="font-size:11px;color:#555;line-height:1.6">
              <p style="margin:0">Filloi: ${fmtTime(tech.clock_in)}</p>
              <p style="margin:0">Kohëzgjatja: ${fmtDuration(tech.elapsed_minutes)}</p>
              <p style="margin:0">Pika GPS: ${tech.location_trail?.length || 0}</p>
            </div>
          </div>
        `);
        markersRef.current.push(marker);
      }
    });

    // Fit map bounds to show all techs
    if (positions.length === 1) {
      map.setView(positions[0], 15);
    } else if (positions.length > 1) {
      map.fitBounds(L.latLngBounds(positions), { padding: [50, 50] });
    }
  }, [techs, fmtTime, fmtDuration]);

  return <div ref={mapContainerRef} style={{ height: '500px', width: '100%' }} data-testid="live-map-container" />;
};

// ===================== ADMIN LIVE TRACKING =====================

const AdminLiveTracking = () => {
  const [activeTechs, setActiveTechs] = useState([]);
  const [clockHistory, setClockHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('live');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [editSession, setEditSession] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [selectedTech, setSelectedTech] = useState(null);
  const reportRef = useRef(null);

  useEffect(() => {
    loadData();
    const interval = setInterval(() => {
      loadActive();
      setLastRefresh(new Date());
    }, 10000); // Refresh every 10 seconds
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadHistory(); }, [selectedDate]);

  const loadData = async () => {
    try {
      const [active, history] = await Promise.all([
        api('/api/admin/clock/active'),
        api('/api/admin/clock/history')
      ]);
      setActiveTechs(active);
      setClockHistory(history);
    } catch { toast.error('Failed to load tracking data'); }
    finally { setLoading(false); }
  };

  const loadActive = async () => {
    try { const data = await api('/api/admin/clock/active'); setActiveTechs(data); }
    catch (e) { console.error('Failed to load active techs', e); }
  };

  const loadHistory = async () => {
    try {
      const data = await api(`/api/admin/clock/history?date=${selectedDate}`);
      setClockHistory(data);
    } catch (e) { console.error('Failed to load clock history', e); }
  };

  const fmtTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const fmtDuration = (mins) => {
    if (!mins) return '0h 0m';
    const h = Math.floor(mins / 60);
    const m = Math.floor(mins % 60);
    const s = Math.floor((mins % 1) * 60);
    return `${h}h ${m}m ${s}s`;
  };

  const openEdit = (session) => {
    setEditSession(session);
    setEditForm({
      clock_in: session.clock_in ? session.clock_in.slice(0, 16) : '',
      clock_out: session.clock_out ? session.clock_out.slice(0, 16) : '',
      lunch_start: session.lunch_start ? session.lunch_start.slice(0, 16) : '',
      lunch_end: session.lunch_end ? session.lunch_end.slice(0, 16) : '',
      notes: session.notes || ''
    });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const body = {};
      if (editForm.clock_in) body.clock_in = new Date(editForm.clock_in).toISOString();
      if (editForm.clock_out) body.clock_out = new Date(editForm.clock_out).toISOString();
      if (editForm.lunch_start) body.lunch_start = new Date(editForm.lunch_start).toISOString();
      if (editForm.lunch_end) body.lunch_end = new Date(editForm.lunch_end).toISOString();
      body.notes = editForm.notes;
      await api(`/api/admin/clock/${editSession.id}`, { method: 'PUT', body: JSON.stringify(body) });
      toast.success('Session updated!');
      setEditSession(null);
      loadHistory();
    } catch (err) { toast.error(err.message || 'Failed to update'); }
    finally { setSaving(false); }
  };

  const downloadPDF = async () => {
    if (!reportRef.current) return;
    setGenerating(true);
    try {
      const canvas = await html2canvas(reportRef.current, { scale: 2, backgroundColor: '#050505', useCORS: true });
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF('l', 'mm', 'a4');
      const w = pdf.internal.pageSize.getWidth();
      const h = (canvas.height * w) / canvas.width;
      let pos = 0;
      const ph = pdf.internal.pageSize.getHeight();
      while (pos < h) { if (pos > 0) pdf.addPage(); pdf.addImage(imgData, 'JPEG', 0, -pos, w, h); pos += ph; }
      pdf.save(`BELLAJA-TimeSheet-${selectedDate}.pdf`);
      toast.success('PDF downloaded!');
    } catch { toast.error('PDF failed'); }
    finally { setGenerating(false); }
  };

  if (loading) return <LoadingSpinner />;

  const totalHours = clockHistory.reduce((s, c) => s + (c.total_hours || 0), 0);
  const regularH = Math.min(totalHours, 40);
  const overtimeH = Math.max(totalHours - 40, 0);

  return (
    <div data-testid="admin-live-tracking" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white">Time Management</h1>
          <p className="text-sm text-gray-500 mt-1">Track, edit, and manage technician work hours</p>
        </div>
        <div className="flex gap-3">
          {tab === 'history' && (
            <button onClick={downloadPDF} disabled={generating} className="btn-primary text-sm flex items-center gap-2" data-testid="download-timesheet-btn">
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}Download PDF
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-2">
        <button onClick={() => setTab('live')} className={`px-5 py-2.5 text-sm rounded-lg font-medium transition-all ${tab === 'live' ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`} data-testid="tab-live">
          Live ({activeTechs.length})
        </button>
        <button onClick={() => setTab('history')} className={`px-5 py-2.5 text-sm rounded-lg font-medium transition-all ${tab === 'history' ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`} data-testid="tab-history">
          Time Sheet
        </button>
      </div>

      {tab === 'live' && (
        <div className="space-y-4">
          {activeTechs.length === 0 ? (
            <div className="card p-12 text-center text-gray-400">
              <User className="w-12 h-12 mx-auto mb-3 text-gray-600" />
              <p className="text-lg">Asnjë teknik nuk është duke punuar aktualisht</p>
              <p className="text-sm text-gray-600 mt-1">Kur një teknik fillon punën, vendndodhja e tij do të shfaqet këtu në kohë reale</p>
            </div>
          ) : (
            <>
              {/* Full Map with all technicians + trails */}
              <div className="rounded-lg bg-gray-900 border border-gray-800 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-800 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse"></div>
                    <p className="text-sm font-bold text-white tracking-wide">HARTA LIVE</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="text-xs text-gray-500">Përditësimi i fundit: {lastRefresh.toLocaleTimeString('en-US', { hour12: false })}</p>
                    <span className="text-xs text-green-500 font-mono">{activeTechs.length} aktiv</span>
                  </div>
                </div>
                <LiveMapView techs={activeTechs} fmtTime={fmtTime} fmtDuration={fmtDuration} />
              </div>

              {/* Tech Cards - Interactive */}
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {activeTechs.map((tech) => (
                  <div
                    key={tech.session_id}
                    className={`rounded-lg bg-gray-900 border p-5 cursor-pointer transition-all hover:border-red-500/50 ${selectedTech === tech.session_id ? 'border-red-500 ring-1 ring-red-500/30' : 'border-gray-800'}`}
                    onClick={() => setSelectedTech(selectedTech === tech.session_id ? null : tech.session_id)}
                    data-testid={`active-tech-${tech.technician_id}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-3 h-3 rounded-full ${tech.status === 'working' ? 'bg-green-500 animate-pulse' : 'bg-yellow-500 animate-pulse'}`}></div>
                        <div>
                          <h3 className="font-bold text-white">{tech.technician_name}</h3>
                          <p className="text-[10px] text-gray-500 uppercase tracking-wider">{tech.status === 'working' ? 'Duke Punuar' : 'Pushim Dreke'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-white font-mono">{fmtDuration(tech.elapsed_minutes)}</p>
                        <p className="text-[10px] text-gray-600">nga {fmtTime(tech.clock_in)}</p>
                      </div>
                    </div>
                    {tech.last_location && (
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-800">
                        <div className="flex items-center gap-1.5">
                          <MapPin className="w-3 h-3 text-red-500" />
                          <p className="text-xs text-gray-500 font-mono">{tech.last_location.lat?.toFixed(5)}, {tech.last_location.lng?.toFixed(5)}</p>
                        </div>
                        <a href={`https://www.google.com/maps?q=${tech.last_location.lat},${tech.last_location.lng}`} target="_blank" rel="noopener noreferrer"
                          className="text-xs text-red-500 hover:text-red-400 flex items-center gap-1 font-medium" data-testid={`map-link-${tech.technician_id}`}>
                          <MapPin className="w-3 h-3" />Google Maps
                        </a>
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-2 text-[10px] text-gray-600">
                      <span>{tech.location_trail?.length || 0} pika GPS të regjistruara</span>
                      {tech.last_location?.time && <span>Update: {fmtTime(tech.last_location.time)}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {tab === 'history' && (
        <div className="space-y-4">
          {/* Date Picker & Stats */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <button onClick={() => { const d = new Date(selectedDate); d.setDate(d.getDate() - 1); setSelectedDate(d.toISOString().split('T')[0]); }} className="btn-secondary text-sm px-3" data-testid="prev-date-btn">
                <ArrowLeft className="w-4 h-4" />
              </button>
              <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
                className="input text-sm w-auto" data-testid="date-picker" />
              <button onClick={() => { const d = new Date(selectedDate); d.setDate(d.getDate() + 1); setSelectedDate(d.toISOString().split('T')[0]); }} className="btn-secondary text-sm px-3">
                <ArrowLeft className="w-4 h-4 rotate-180" />
              </button>
            </div>
            <div className="flex gap-4 text-sm">
              <span className="text-gray-400">{clockHistory.length} sessions</span>
              <span className="text-white font-medium">{totalHours.toFixed(2)}h total</span>
              {overtimeH > 0 && <span className="text-orange-400 font-medium">{overtimeH.toFixed(2)}h OT (1.5x)</span>}
            </div>
          </div>

          {/* Time Sheet Table */}
          <div ref={reportRef} className="bg-[#050505] rounded-lg border border-gray-800 overflow-hidden">
            {/* Report Header */}
            <div className="p-6 border-b border-gray-800 bg-gradient-to-r from-red-900/20 to-transparent">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white tracking-wider">BELLAJA</h2>
                  <p className="text-red-500 text-[10px] tracking-[0.3em]">ELECTRICAL & SECURITY SYSTEMS</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-gray-600 tracking-widest">TIME SHEET REPORT</p>
                  <p className="text-sm text-white font-medium">{fmtDate(selectedDate)}</p>
                </div>
              </div>
            </div>

            <table className="w-full">
              <thead>
                <tr className="bg-gray-900">
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-red-500 tracking-widest">TECHNICIAN</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-red-500 tracking-widest">STATUS</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-red-500 tracking-widest">CLOCK IN</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-red-500 tracking-widest">LUNCH START</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-red-500 tracking-widest">LUNCH END</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-red-500 tracking-widest">CLOCK OUT</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-red-500 tracking-widest">WORK TIME</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-red-500 tracking-widest">HOURS</th>
                  <th className="px-4 py-3 text-center text-[10px] font-bold text-red-500 tracking-widest">EDIT</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/50">
                {clockHistory.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-900/50 transition-colors" data-testid={`clock-row-${s.id}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-white text-sm">{s.technician_name}</p>
                      {s.edited_at && <p className="text-[10px] text-yellow-500">edited</p>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-2 py-1 rounded-full font-bold tracking-wider ${
                        s.status === 'working' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                        s.status === 'lunch' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                        s.status === 'finished' ? 'bg-gray-500/20 text-gray-400 border border-gray-500/30' :
                        'bg-gray-800 text-gray-500'
                      }`}>{s.status?.toUpperCase()}</span>
                    </td>
                    <td className="px-4 py-3 font-mono text-sm text-white">{fmtTime(s.clock_in)}</td>
                    <td className="px-4 py-3 font-mono text-sm text-gray-400">{fmtTime(s.lunch_start)}</td>
                    <td className="px-4 py-3 font-mono text-sm text-gray-400">{fmtTime(s.lunch_end)}</td>
                    <td className="px-4 py-3 font-mono text-sm text-white">{fmtTime(s.clock_out)}</td>
                    <td className="px-4 py-3 font-mono text-sm text-gray-300">{fmtDuration(s.total_work_minutes)}</td>
                    <td className="px-4 py-3 font-bold text-white text-sm">{s.total_hours ? `${s.total_hours}h` : '—'}</td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => openEdit(s)} className="text-xs text-blue-400 hover:text-blue-300 font-medium" data-testid={`edit-session-${s.id}`}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {clockHistory.length === 0 && (
              <div className="p-12 text-center text-gray-500 text-sm">No clock sessions for this date.</div>
            )}

            {/* Totals Footer */}
            {clockHistory.length > 0 && (
              <div className="p-4 border-t border-gray-800 bg-gray-900/50 flex items-center justify-between">
                <div className="flex gap-6 text-xs">
                  <span className="text-gray-500">NYC Labor Standards Applied</span>
                  <span className="text-gray-500">OT after 40h/week = 1.5x rate</span>
                </div>
                <div className="flex gap-6 text-sm">
                  <span className="text-gray-400">Total: <span className="text-white font-bold">{totalHours.toFixed(2)}h</span></span>
                  {overtimeH > 0 && <span className="text-orange-400 font-medium">OT: {overtimeH.toFixed(2)}h</span>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Session Modal */}
      {editSession && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="card p-6 max-w-lg w-full" data-testid="edit-session-modal">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-heading text-xl font-bold text-white">Edit Clock Session</h3>
                <p className="text-sm text-gray-500 mt-1">{editSession.technician_name} — {fmtDate(editSession.clock_in)}</p>
              </div>
              <button onClick={() => setEditSession(null)} className="p-2 hover:bg-gray-800 rounded"><X className="w-5 h-5 text-gray-400" /></button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 font-bold block mb-1">CLOCK IN</label>
                  <input type="datetime-local" className="input text-sm" value={editForm.clock_in}
                    onChange={e => setEditForm({ ...editForm, clock_in: e.target.value })} data-testid="edit-clock-in" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-bold block mb-1">CLOCK OUT</label>
                  <input type="datetime-local" className="input text-sm" value={editForm.clock_out}
                    onChange={e => setEditForm({ ...editForm, clock_out: e.target.value })} data-testid="edit-clock-out" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-bold block mb-1">LUNCH START</label>
                  <input type="datetime-local" className="input text-sm" value={editForm.lunch_start}
                    onChange={e => setEditForm({ ...editForm, lunch_start: e.target.value })} data-testid="edit-lunch-start" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-bold block mb-1">LUNCH END</label>
                  <input type="datetime-local" className="input text-sm" value={editForm.lunch_end}
                    onChange={e => setEditForm({ ...editForm, lunch_end: e.target.value })} data-testid="edit-lunch-end" />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 font-bold block mb-1">NOTES (reason for edit)</label>
                <textarea className="input min-h-[60px] text-sm" value={editForm.notes} placeholder="E.g. Technician forgot to clock out..."
                  onChange={e => setEditForm({ ...editForm, notes: e.target.value })} data-testid="edit-notes" />
              </div>
              <div className="bg-gray-800/50 rounded p-3 text-xs text-gray-400">
                <p>Changes will recalculate total work hours automatically.</p>
                <p className="text-yellow-500 mt-1">Edited sessions are marked in the time sheet.</p>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setEditSession(null)} className="btn-secondary flex-1">Cancel</button>
                <button onClick={saveEdit} disabled={saving} className="btn-primary flex-1" data-testid="save-edit-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : null}Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ===================== ADMIN PAYROLL =====================


export default AdminLiveTracking;
